export class ProductInfo extends HTMLElement {
  abortController = undefined;
  swiper = undefined;

  constructor() {
    super();
  }

  setupEventListeners() {
    this.variantSelector?.addEventListener('change', this.onVariantChange.bind(this));
    if (!this.quantitySelector) return;
    this.quantitySelector.addEventListener('change', this.onQuantitySelectorEvent.bind(this));
    this.quantitySelector.querySelector('button[name="plus"]')?.addEventListener('click', this.onQuantitySelectorEvent.bind(this));
    this.quantitySelector.querySelector('button[name="minus"]')?.addEventListener('click', this.onQuantitySelectorEvent.bind(this));
  }

  connectedCallback() { 
    this.setupEventListeners();
    this.initSwiper();
    this.releasePostscriptBis();
    this.observePostscriptBis();
  }

  disconnectedCallback() {
    this.postscriptObserver?.disconnect();
  }

  initSwiper() {
    const thumbnailGalleryEl = this.querySelector('.product-media-gallery__thumbnails');
    if (thumbnailGalleryEl) {
      const mainGalleryEl = this.querySelector('.product-media-gallery__main');
      if (mainGalleryEl) {
        const prevEl = mainGalleryEl.querySelector('.product-media-gallery__nav--prev');
        const nextEl = mainGalleryEl.querySelector('.product-media-gallery__nav--next');
        const thumbsSwiper = new Swiper(thumbnailGalleryEl, {
          spaceBetween: 8,
          slidesPerView: 'auto',
          freeMode: true,
          watchSlidesProgress: true,
          slideToClickedSlide: true,
          watchOverflow: true,
          breakpoints: {
            768: {
              spaceBetween: 16,
            },
          },
        });
        const mainConfig = {
          spaceBetween: 0,
          watchOverflow: true,
          thumbs: {
            swiper: thumbsSwiper,
            autoScrollOffset: 1,
          },
        };

        if (prevEl && nextEl) {
          mainConfig.navigation = {
            prevEl,
            nextEl,
          };
        }

        this.swiper = new Swiper(mainGalleryEl, mainConfig);
      }
    }

    const carouselGalleryEl = this.querySelector('.product-media-gallery__carousel');
    if (carouselGalleryEl) {
      this.swiper = new Swiper(carouselGalleryEl, {
        autoHeight: true,
        direction: 'horizontal',
        pagination: {
          el: carouselGalleryEl.querySelector('.swiper-pagination'),
        },
        navigation: {
          prevEl: carouselGalleryEl.querySelector('.swiper-button-prev'),
          nextEl: carouselGalleryEl.querySelector('.swiper-button-next'),
        },
      });
    }
  }

  get variantSelector() {
    return this.querySelector('variant-selector');
  }

  get quantitySelector() {
    return this.querySelector('quantity-selector');
  }

  get selectedOptionValues() {
    if (this.variantSelector.dataset.pickerType === 'dropdown') {
      const list = Array.from(this.variantSelector.querySelectorAll('select')).map(
        (select) => select.options[select.selectedIndex].dataset.optionValueId
      );
      return list;
    } else {
      const list = Array.from(this.variantSelector.querySelectorAll('fieldset input:checked')).map(
        ({ dataset }) => dataset.optionValueId
      );
      return list;
    }
  }

  getSelectedVariant(html) {
    const selectedVariant = html.querySelector('[data-selected-variant]')?.innerHTML;
    return !!selectedVariant ? JSON.parse(selectedVariant) : null;
  }

  onVariantChange(e) {
    const productUrlChanged = e.target?.dataset?.productUrl ? (e.target?.dataset?.productUrl !== this.dataset.url) : false;
    const productUrl = e.target?.dataset?.productUrl || this.dataset.url;
    this.renderSection(productUrlChanged, productUrl);
  }

  onQuantitySelectorEvent(e) {
    const quantityInput = this.quantitySelector.querySelector('input[type="number"]');
    let currentValue = parseInt(quantityInput.value);
    const minValue = parseInt(quantityInput.getAttribute('min')) || 0;
    const maxValue = parseInt(quantityInput.getAttribute('max')) || Infinity;
    const actionName = e.currentTarget?.name || e.target?.closest('button')?.name;

    if (actionName === 'minus' && currentValue > minValue) {
      quantityInput.value = currentValue - 1;
    } else if (actionName === 'plus' && currentValue < maxValue) {
      quantityInput.value = currentValue + 1;
    } else if (e.type === 'change') {
      if (currentValue < minValue) {
        quantityInput.value = minValue;
      } else if (currentValue > maxValue) {
        quantityInput.value = maxValue;
      }
    }
  }

  updateMedia(variantFeaturedMediaId) {
    if (!variantFeaturedMediaId) return;
    const mediaSlide = this.querySelector(`.swiper-slide[data-media-id="${variantFeaturedMediaId}"]`);
    if (!mediaSlide) return;

    const slideIndex = parseInt(mediaSlide.dataset.mediaIndex);
    this.swiper?.slideTo(slideIndex);
  }

  updateURL(variantId) {
    // this.querySelector('share-button')?.updateUrl(
    //   `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
    // );

    // Don't update URL if this is in a modal/quick-add context
    if (this.dataset.updateUrl === 'false') return;

    if (!window.location.pathname.includes('/products/')) return;
    window.history.replaceState({}, '', `${this.dataset.url}${variantId ? `?variant=${variantId}` : ''}`);
  }

  updateSourceFromDestination = (html, id) => {
    const source = html.getElementById(`${id}`);
    const destination = this.querySelector(`#${id}`);
    if (source && destination) {
      destination.innerHTML = source.innerHTML;
    }
  };

  updateVariantInputs(variantId) {
    this.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`).forEach(
      (productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        input.value = variantId ?? '';
      }
    );
  }

  renderSection(productUrlChanged, productUrl) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    // If the section is in a modal, use the original section id without the modal suffix
    const sectionId = this.dataset.updateUrl === 'false' ? this.dataset.section.split('-modal')[0] : this.dataset.section;
    fetch(`${productUrl}?option_values=${this.selectedOptionValues}&section_id=${sectionId}`, {
      signal: this.abortController.signal,
    })
      .then((response) => response.text())
      .then((responseText) => {
        // If the section is in a modal, replace the original section id with the modal section id
        if (this.dataset.updateUrl === 'false') {
          responseText = responseText.replaceAll(this.dataset.section.split('-modal')[0], this.dataset.section);
        }

        // Parse the response text into an HTML document
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const variant = this.getSelectedVariant(html);
        if (productUrlChanged) {
          // If the product url has changed, replace the current section with the new section
          const productInfo = html.querySelector('product-info');
          this.replaceWith(productInfo);
          productInfo.updateURL(variant?.id);
          productInfo.dispatchEvent(
            new CustomEvent('variant:change', {
              bubbles: true,
              detail: { variant },
            })
          );
        } else {
          this.updateMedia(variant?.featured_media?.id);
          this.updateURL(variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateSourceFromDestination(html, `add-to-cart-container-${this.dataset.section}`);
          this.updateSourceFromDestination(html, `variant-selector-${this.dataset.section}`);
          this.updateSourceFromDestination(html, `price-${this.dataset.section}`);
          this.updateSourceFromDestination(html, `sku-${this.dataset.section}`);
          this.updateSourceFromDestination(html, `inventory-${this.dataset.section}`);
          this.toggleNotifyMe(variant);
          this.dispatchEvent(
            new CustomEvent('variant:change', {
              bubbles: true,
              detail: { variant },
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  toggleNotifyMe(variant) {
    const available = Boolean(variant?.available);
    const bis = this.querySelector('.product-form__bis');
    if (!bis) return;

    const notifyButton = bis.querySelector('.section-main-product-notify-me');
    bis.classList.toggle('is-hidden', available);
    bis.toggleAttribute('hidden', available);

    if (notifyButton) {
      if (available) {
        notifyButton.setAttribute('tabindex', '-1');
        notifyButton.setAttribute('aria-hidden', 'true');
      } else {
        notifyButton.setAttribute('tabindex', '0');
        notifyButton.removeAttribute('aria-hidden');
      }
    }

    this.releasePostscriptBis();
  }

  releasePostscriptBis() {
    const root = document.getElementById('ps__bis_container_root');
    if (!root) return;

    if (root.closest('.product-form__bis, .product-form__actions, .product-form__buttons, product-info')) {
      document.body.appendChild(root);
    }
  }

  observePostscriptBis() {
    if (this.postscriptObserver) return;

    this.postscriptObserver = new MutationObserver(() => {
      this.releasePostscriptBis();
    });

    this.postscriptObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
}

if (!customElements.get('product-info')) {
  customElements.define('product-info', ProductInfo);
}